import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Control from "../../common/Control";
import InfoBulle from "../../common/InfoBulle";
import PostalCode from "../../common/PostalCode";
import { FactList, FicheSection, StickySaveBar } from "../../common/Fiche";
import CartesRegimes from "./CartesRegimes";
import { modifierOperateur } from "./operateursApi";
import { REGIMES_ACCOMPAGNEMENT, libelleStatut } from "./libellesOperateur";

// ============================================================
// Onglet Informations de la fiche opérateur : identité, régimes exercés,
// coordonnées du siège, délai de report des rendez-vous. Le PATCH renvoie la
// fiche à jour : cocher un premier régime passe un « Actif » en préparation,
// retirer le dernier le ramène « Actif » — le changement de statut est dit en
// notification. Erreurs 400/419 sous les champs, refus 409 en bandeau. Le
// rattachement n'est pas modifiable : toute structure est rattachée à la Caf.
// En `lectureSeule` (vue de l'opérateur connecté, CE-1407), les mêmes
// sections se lisent en faits, sans champ ni enregistrement.
// ============================================================

const OngletInformationsLecture = ({ fiche }) => {
  const regimes = fiche.regimes || [];
  const siege = [fiche.adresse, [fiche.codePostal, fiche.ville].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return (
    <div className="fiche-operateur__grille">
      <FicheSection id="operateur-identite" title="Identité de la structure">
        <FactList
          facts={[
            { id: "nom", label: "Nom de la structure", value: fiche.nom },
            { id: "sigle", label: "Sigle", value: fiche.sigle, mono: true },
          ]}
          emptyLabel="Non renseigné"
        />
      </FicheSection>

      <FicheSection id="operateur-regimes" title="Régimes d'accompagnement">
        {regimes.length ? (
          <ul className="regimes-lecture">
            {regimes.map((r) => {
              const regime = REGIMES_ACCOMPAGNEMENT[r] || { titre: r, description: "" };
              return (
                <li key={r}>
                  <span className="regimes-lecture__titre">{regime.titre}</span>
                  {regime.description && <span className="regimes-lecture__description">{regime.description}</span>}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="fiche-operateur__secondaire">Aucun régime : opérateur du répertoire.</p>
        )}
        {regimes.length > 0 && (
          <FactList
            className="mt-3"
            facts={[
              {
                id: "delai",
                label: "Délai minimum pour reporter un rendez-vous",
                value: `${fiche.delaiReportRdvJours == null ? 3 : fiche.delaiReportRdvJours} jour${fiche.delaiReportRdvJours > 1 ? "s" : ""} avant la date`,
              },
            ]}
          />
        )}
      </FicheSection>

      <FicheSection id="operateur-coordonnees" title="Coordonnées du siège">
        <FactList
          facts={[
            { id: "siege", label: "Adresse", value: siege },
            { id: "telephone", label: "Téléphone", value: fiche.telephone, mono: true },
            { id: "email", label: "E-mail de contact", value: fiche.email, truncate: true },
            { id: "lien", label: "Site internet", value: fiche.lien, truncate: true },
          ]}
          emptyLabel="Non renseigné"
        />
      </FicheSection>
      <p className="fiche-operateur__secondaire fiche-operateur__pleine-largeur">
        Ces informations sont tenues par la Caf : signalez-lui toute correction.
      </p>
    </div>
  );
};

/** Au survol du « i » du libellé : le sigle n'est ni obligatoire ni un identifiant. */
const AIDE_SIGLE =
  "Abréviation facultative, par exemple AREP : elle aide à retrouver la structure et à repérer un doublon.";

const formulaireDepuisFiche = (fiche) => ({
  nom: fiche.nom || "",
  sigle: fiche.sigle || "",
  regimes: fiche.regimes || [],
  adresse: fiche.adresse || "",
  villeId: fiche.villeId || null,
  location: fiche.villeId ? { postalCode: fiche.codePostal, name: fiche.ville } : null,
  telephone: fiche.telephone || "",
  email: fiche.email || "",
  lien: fiche.lien || "",
  delaiReportRdvJours: fiche.delaiReportRdvJours == null ? 3 : fiche.delaiReportRdvJours,
});

const OngletInformations = ({ fiche, onFicheModifiee, lectureSeule = false }) => {
  if (lectureSeule) return <OngletInformationsLecture fiche={fiche} />;
  return <OngletInformationsFormulaire fiche={fiche} onFicheModifiee={onFicheModifiee} />;
};

const OngletInformationsFormulaire = ({ fiche, onFicheModifiee }) => {
  const [formulaire, setFormulaire] = useState(() => formulaireDepuisFiche(fiche));
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [modifie, setModifie] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    setFormulaire(formulaireDepuisFiche(fiche));
    setModifie(false);
    setErreurs({});
    setRefus(null);
  }, [fiche]);

  const changer = (e) => {
    const { name, value, other } = e.target;
    setFormulaire((f) =>
      name === "villeId" ? { ...f, villeId: value, location: other || f.location } : { ...f, [name]: value }
    );
    setErreurs((er) => ({ ...er, [name]: undefined }));
    setModifie(true);
  };

  const basculerRegime = (regime) => {
    setFormulaire((f) => ({
      ...f,
      regimes: f.regimes.includes(regime)
        ? f.regimes.filter((r) => r !== regime)
        : [...f.regimes, regime],
    }));
    setModifie(true);
  };

  const enregistrer = () => {
    // Le Domain accepte un délai « positif ou nul » : 0 est une valeur, pas
    // une absence. Un champ vidé part en null, que l'API refuse sous le champ.
    const saisieDelai = String(formulaire.delaiReportRdvJours ?? "").trim();
    const delai = saisieDelai === "" ? NaN : Number(saisieDelai);
    const corps = {
      nom: formulaire.nom.trim(),
      sigle: formulaire.sigle.trim() || null,
      regimes: formulaire.regimes,
      adresse: formulaire.adresse.trim() || null,
      villeId: formulaire.villeId || null,
      telephone: formulaire.telephone.trim() || null,
      email: formulaire.email.trim() || null,
      lien: formulaire.lien.trim() || null,
      delaiReportRdvJours: Number.isInteger(delai) && delai >= 0 ? delai : null,
    };
    setEnregistrement(true);
    setErreurs({});
    setRefus(null);
    modifierOperateur(fiche.id, corps).then(
      (ficheAJour) => {
        setEnregistrement(false);
        setModifie(false);
        toast.success("Modifications enregistrées.");
        if (ficheAJour.statut !== fiche.statut) {
          toast.info(`L'opérateur passe au statut « ${libelleStatut(ficheAJour.statut)} ».`);
        }
        onFicheModifiee(ficheAJour);
      },
      (erreur) => {
        setEnregistrement(false);
        if (erreur.champs) setErreurs(erreur.champs);
        else setRefus(erreur.message || "Une erreur est survenue");
      }
    );
  };

  const avecRegime = formulaire.regimes.length > 0;

  return (
    <div className="fiche-operateur__grille">
      {refus && (
        <div className="alert alert-danger fiche-operateur__pleine-largeur" role="alert">
          {refus}
        </div>
      )}

      <FicheSection id="operateur-identite" title="Identité de la structure">
        <div className="fiche-operateur__champs">
          <Control
            margin="my-0 fiche-operateur__champ-large"
            label="Nom de la structure"
            name="nom"
            value={formulaire.nom}
            change={changer}
            error={erreurs}
            required
            inputProps={{ autoComplete: "off" }}
          />
          <Control
            margin="my-0"
            label={
              <>
                Sigle <InfoBulle texte={AIDE_SIGLE} />
              </>
            }
            name="sigle"
            value={formulaire.sigle}
            change={changer}
            error={erreurs}
            inputProps={{ autoComplete: "off" }}
          />
        </div>
      </FicheSection>

      <FicheSection id="operateur-regimes" title="Régimes d'accompagnement">
        <p className="fiche-operateur__secondaire">
          Un opérateur peut exercer les deux régimes ; le régime se choisit ensuite dossier par
          dossier.
        </p>
        <fieldset className="groupe-sans-bordure">
          <legend className="sr-only">Régimes exercés</legend>
          <CartesRegimes
            regimes={formulaire.regimes}
            onBasculer={basculerRegime}
            erreur={erreurs.regimes}
            idPrefix="informations-"
          />
        </fieldset>
        {avecRegime && (
          <div className="fiche-operateur__delai">
            <Control
              margin="my-0"
              label="Délai minimum pour reporter un rendez-vous (en jours avant la date)"
              type="number"
              name="delaiReportRdvJours"
              value={formulaire.delaiReportRdvJours}
              change={changer}
              error={erreurs}
            />
          </div>
        )}
      </FicheSection>

      <FicheSection id="operateur-coordonnees" title="Coordonnées du siège">
        <div className="fiche-operateur__champs">
          <Control
            margin="my-0 fiche-operateur__champ-large"
            label="Adresse"
            name="adresse"
            value={formulaire.adresse}
            change={changer}
            error={erreurs}
            inputProps={{ autoComplete: "off" }}
          />
          <PostalCode
            inputLabel="Commune du siège"
            value={formulaire.villeId}
            name="villeId"
            inputId="operateur-code-postal"
            updateData={changer}
            error={erreurs}
            location={formulaire.location}
          />
          <Control
            margin="my-0"
            label="Téléphone"
            type="tel"
            name="telephone"
            value={formulaire.telephone}
            change={changer}
            error={erreurs}
          />
          <Control
            margin="my-0"
            label="E-mail de contact"
            type="email"
            name="email"
            value={formulaire.email}
            change={changer}
            error={erreurs}
          />
          <Control
            margin="my-0"
            label="Site internet"
            name="lien"
            placeholder="https://"
            value={formulaire.lien}
            change={changer}
            error={erreurs}
          />
        </div>
      </FicheSection>

      <StickySaveBar
        visible={modifie}
        isSaving={enregistrement}
        onSave={enregistrer}
        message="Modifications non enregistrées"
        label="Enregistrer"
      />
    </div>
  );
};

export default OngletInformations;
